// ─────────────────────────────────────────────────────────────────────────────
// Glide: a scroll engine that leaves scrolling to the browser.
//
// The viewport is an ordinary `overflow: auto` element with its native bars
// hidden. Wheel, trackpad, touch momentum, pinch zoom, keyboard, scroll-snap
// and nested propagation are therefore exactly what the browser does; this
// class draws the bars, keeps them in step, observes size so nobody has to
// call update(), and reports state and events the browser will not.
//
// Every DOM write happens inside one animation frame per change, after every
// read, so a busy scroll never thrashes layout.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  GlideAxis, GlideBehavior, GlideEvent, GlideEventName, GlideHandler, GlideMarker, GlideOptions,
  GlideScrollToElementOptions, GlideScrollToOptions, GlideSource, GlideState, GlideVisibility
} from './types.js';
import './glide.css';

type Resolved = Required<Omit<GlideOptions, 'scrollbarSize' | 'scrollShadows' | 'infiniteScroll' | 'events' | 'label' | 'markers'>> & {
  scrollbarSize: { normal?: number; hover?: number; touch?: number } | null;
  scrollShadows: { top: boolean; bottom: boolean; left: boolean; right: boolean };
  infiniteScroll: NonNullable<GlideOptions['infiniteScroll']> | null;
  throttle: 'frame' | 'none' | number;
  label: string | null;
  markers: GlideMarker[];
};

const DEFAULTS: Resolved = {
  // Vertical by default. `both` made every container's content refuse to
  // wrap (see glide.css), so modals, drawers and both shells grew a horizontal
  // bar they never asked for. Horizontal is opted into with `x` or `both`.
  axis: 'y',
  visibility: 'auto',
  scrollbarMode: 'overlay',
  verticalPosition: 'auto',
  horizontalPosition: 'bottom',
  scrollbarSize: null,
  thumbHitArea: 16,
  minThumbLength: 24,
  maxThumbLength: Infinity,
  overflowTolerance: 1,
  autoUpdate: true,
  scrollShadows: { top: false, bottom: false, left: false, right: false },
  stickToBottom: false,
  preserveScrollPosition: false,
  infiniteScroll: null,
  overscroll: 'auto',
  theme: 'auto',
  throttle: 'frame',
  scrollingThreshold: 600,
  focusable: true,
  label: null,
  markers: [],
  debug: false
};

const EDGE = 1;

function el(tag: string, className: string, parent: HTMLElement): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  parent.appendChild(node);
  return node;
}

function resolve(options: GlideOptions): Resolved {
  const shadows = options.scrollShadows === true
    ? { top: true, bottom: true, left: true, right: true }
    : options.scrollShadows && typeof options.scrollShadows === 'object'
      ? { top: !!options.scrollShadows.top, bottom: !!options.scrollShadows.bottom, left: !!options.scrollShadows.left, right: !!options.scrollShadows.right }
      : DEFAULTS.scrollShadows;
  const size = options.scrollbarSize == null ? null
    : typeof options.scrollbarSize === 'number' ? { normal: options.scrollbarSize }
    : options.scrollbarSize;
  return {
    ...DEFAULTS,
    ...stripUndefined(options),
    scrollbarSize: size,
    scrollShadows: shadows,
    infiniteScroll: options.infiniteScroll ?? null,
    throttle: options.events?.throttle ?? 'frame',
    label: options.label ?? null,
    markers: options.markers ?? []
  } as Resolved;
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of Object.keys(o) as Array<keyof T>) if (o[k] !== undefined) out[k] = o[k];
  return out;
}

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export class Glide {
  readonly host: HTMLElement;
  readonly viewport: HTMLElement;
  readonly content: HTMLElement;

  private opts: Resolved;
  private readonly railY: HTMLElement;
  private readonly thumbY: HTMLElement;
  private readonly railX: HTMLElement;
  private readonly thumbX: HTMLElement;
  private readonly markersEl: HTMLElement;
  private shadowEls: Partial<Record<'top' | 'bottom' | 'left' | 'right', HTMLElement>> = {};
  private debugEl: HTMLElement | null = null;
  private readonly wrappedChildren: boolean;

  private state: GlideState;
  private track = { y: 0, x: 0, thumbY: 0, thumbX: 0 };
  private handlers = new Map<GlideEventName, Set<GlideHandler>>();
  private frame: number | null = null;
  private frameQueued = false;
  private pendingMeasure = false;
  private ro: ResizeObserver | null = null;
  private mo: MutationObserver | null = null;
  private scrollingTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSource: GlideSource = 'unknown';
  private lastEmit = 0;
  private hovering = false;
  private focused = false;
  private drag: { axis: 'x' | 'y'; start: number; startScroll: number } | null = null;
  private wasAtBottom = true;
  private prevScrollHeight = 0;
  private addedAbove = false;
  private notified: { height: number | null; width: number | null } = { height: null, width: null };
  private infinite = { endHeight: -1, startHeight: -1, pending: false };
  private destroyed = false;
  private readonly cleanups: Array<() => void> = [];

  constructor(host: HTMLElement, options: GlideOptions = {}) {
    this.host = host;
    this.opts = resolve(options);
    host.classList.add('rr-glide');

    // The React surface renders the viewport and content itself; a plain
    // element gets them built here, with its children moved inside.
    let viewport = host.querySelector<HTMLElement>(':scope > .rr-glide__viewport');
    let content = viewport?.querySelector<HTMLElement>(':scope > .rr-glide__content') ?? null;
    if (!viewport) {
      viewport = document.createElement('div');
      viewport.className = 'rr-glide__viewport';
      content = document.createElement('div');
      content.className = 'rr-glide__content';
      while (host.firstChild) content.appendChild(host.firstChild);
      viewport.appendChild(content);
      host.appendChild(viewport);
      this.wrappedChildren = true;
    } else {
      this.wrappedChildren = false;
      if (!content) {
        content = document.createElement('div');
        content.className = 'rr-glide__content';
        while (viewport.firstChild) content.appendChild(viewport.firstChild);
        viewport.appendChild(content);
      }
    }
    this.viewport = viewport;
    this.content = content;

    this.railY = el('div', 'rr-glide__rail rr-glide__rail--y', host);
    this.thumbY = el('div', 'rr-glide__thumb rr-glide__thumb--y', this.railY);
    this.markersEl = el('div', 'rr-glide__markers', this.railY);
    this.railX = el('div', 'rr-glide__rail rr-glide__rail--x', host);
    this.thumbX = el('div', 'rr-glide__thumb rr-glide__thumb--x', this.railX);
    for (const rail of [this.railY, this.railX]) rail.setAttribute('aria-hidden', 'true');

    this.state = {
      scrollTop: 0, scrollLeft: 0, maxScrollTop: 0, maxScrollLeft: 0,
      clientWidth: 0, clientHeight: 0, scrollWidth: 0, scrollHeight: 0,
      progressX: 0, progressY: 0, canScrollX: false, canScrollY: false,
      atTop: true, atBottom: true, atLeft: true, atRight: true,
      scrolling: false, direction: null, rtl: false
    };

    this.applyOptions();
    this.bind();
    this.observe();
    this.update();
    if (this.opts.stickToBottom === 'always') this.scrollToBottom('instant');
  }

  // ── Public API ───────────────────────────────────────────────────────────

  getOptions(): GlideOptions {
    const o = this.opts;
    return {
      axis: o.axis, visibility: o.visibility, scrollbarMode: o.scrollbarMode, verticalPosition: o.verticalPosition,
      horizontalPosition: o.horizontalPosition, scrollbarSize: o.scrollbarSize ?? undefined, thumbHitArea: o.thumbHitArea,
      minThumbLength: o.minThumbLength, maxThumbLength: o.maxThumbLength, overflowTolerance: o.overflowTolerance,
      autoUpdate: o.autoUpdate, scrollShadows: o.scrollShadows, stickToBottom: o.stickToBottom,
      preserveScrollPosition: o.preserveScrollPosition, infiniteScroll: o.infiniteScroll ?? undefined, overscroll: o.overscroll,
      theme: o.theme, events: { throttle: o.throttle }, scrollingThreshold: o.scrollingThreshold, focusable: o.focusable,
      label: o.label ?? undefined, markers: o.markers, debug: o.debug
    };
  }

  /** Change anything at runtime. No destroy and recreate. */
  setOptions(next: GlideOptions): void {
    const before = this.opts;
    this.opts = resolve({ ...this.getOptions(), ...next, events: { throttle: next.events?.throttle ?? before.throttle } });
    this.applyOptions();
    if (before.autoUpdate !== this.opts.autoUpdate) {
      this.unobserve();
      this.observe();
    }
    this.update();
  }

  getState(): GlideState {
    return { ...this.state };
  }

  get progress(): { x: number; y: number } {
    return { x: this.state.progressX, y: this.state.progressY };
  }

  get isAtBottom(): boolean {
    return this.state.atBottom;
  }

  /** Re-measure now. With `autoUpdate` (the default) this is rarely needed. */
  update(): void {
    if (this.destroyed) return;
    this.pendingMeasure = false;
    this.measure();
    this.paint();
    this.afterMeasure();
  }

  /**
   * Tell the engine how big the content really is, for a virtualised list
   * whose DOM is only the visible slice. Pass null to go back to measuring.
   */
  notifyContentSize(size: { height?: number | null; width?: number | null }): void {
    if (size.height !== undefined) this.notified.height = size.height;
    if (size.width !== undefined) this.notified.width = size.width;
    this.scheduleUpdate();
  }

  scrollTo(target: GlideScrollToOptions): void {
    this.lastSource = 'programmatic';
    this.viewport.scrollTo({
      top: target.top,
      left: target.left === undefined ? undefined : this.toPhysicalLeft(target.left),
      behavior: this.behavior(target.behavior)
    });
  }

  scrollToTop(behavior?: GlideBehavior): void { this.scrollTo({ top: 0, behavior }); }
  scrollToBottom(behavior?: GlideBehavior): void { this.scrollTo({ top: this.viewport.scrollHeight, behavior }); }
  scrollToLeft(behavior?: GlideBehavior): void { this.scrollTo({ left: 0, behavior }); }
  scrollToRight(behavior?: GlideBehavior): void { this.scrollTo({ left: this.viewport.scrollWidth, behavior }); }

  scrollToElement(target: Element, options: GlideScrollToElementOptions = {}): void {
    const v = this.viewport;
    const vr = v.getBoundingClientRect();
    const r = target.getBoundingClientRect();
    const offset = options.offset ?? 0;
    const align = options.align ?? 'start';
    const relTop = r.top - vr.top + v.scrollTop;
    const relLeft = r.left - vr.left + this.logicalLeft();
    let top: number | undefined;
    let left: number | undefined;
    if (this.opts.axis !== 'x') {
      top = align === 'center' ? relTop - (v.clientHeight - r.height) / 2
        : align === 'end' ? relTop - v.clientHeight + r.height + offset
        : align === 'nearest' ? nearest(v.scrollTop, v.clientHeight, relTop, r.height, offset)
        : relTop - offset;
    }
    if (this.opts.axis !== 'y') {
      left = align === 'center' ? relLeft - (v.clientWidth - r.width) / 2
        : align === 'end' ? relLeft - v.clientWidth + r.width + offset
        : align === 'nearest' ? nearest(this.logicalLeft(), v.clientWidth, relLeft, r.width, offset)
        : relLeft - offset;
    }
    this.scrollTo({ top, left, behavior: options.behavior });
  }

  setMarkers(markers: GlideMarker[]): void {
    this.opts.markers = markers;
    this.paintMarkers();
  }

  on(name: GlideEventName, handler: GlideHandler): () => void {
    let set = this.handlers.get(name);
    if (!set) { set = new Set(); this.handlers.set(name, set); }
    set.add(handler);
    return () => { set?.delete(handler); };
  }

  off(name: GlideEventName, handler: GlideHandler): void {
    this.handlers.get(name)?.delete(handler);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.unobserve();
    for (const c of this.cleanups) c();
    if (this.frame != null) cancelAnimationFrame(this.frame);
    if (this.scrollingTimer) clearTimeout(this.scrollingTimer);
    this.railY.remove();
    this.railX.remove();
    for (const s of Object.values(this.shadowEls)) s?.remove();
    this.debugEl?.remove();
    if (this.wrappedChildren) {
      while (this.content.firstChild) this.host.appendChild(this.content.firstChild);
      this.viewport.remove();
    }
    this.host.classList.remove('rr-glide', 'is-dragging', 'is-scrolling', 'is-hover', 'is-focus');
    for (const a of Array.from(this.host.attributes)) if (a.name.startsWith('data-glide-')) this.host.removeAttribute(a.name);
    this.handlers.clear();
  }

  // ── Options → DOM ────────────────────────────────────────────────────────

  private applyOptions(): void {
    const o = this.opts;
    const h = this.host;
    h.setAttribute('data-glide-axis', o.axis);
    h.setAttribute('data-glide-mode', o.scrollbarMode);
    h.setAttribute('data-glide-theme', o.theme);
    h.setAttribute('data-glide-x', o.horizontalPosition);
    h.style.setProperty('--rr-glide-overscroll', o.overscroll);
    h.style.setProperty('--rr-glide-hit', `${o.thumbHitArea}px`);
    const size = o.scrollbarSize;
    h.style.setProperty('--rr-glide-size', size?.normal != null ? `${size.normal}px` : '');
    h.style.setProperty('--rr-glide-size-hover', size?.hover != null ? `${size.hover}px` : size?.normal != null ? `${size.normal + 2}px` : '');
    h.style.setProperty('--rr-glide-size-touch', size?.touch != null ? `${size.touch}px` : '');

    // Shadows exist only when asked for; an empty div per edge is not free.
    for (const side of ['top', 'bottom', 'left', 'right'] as const) {
      const want = o.scrollShadows[side];
      const have = this.shadowEls[side];
      if (want && !have) this.shadowEls[side] = el('div', `rr-glide__shadow rr-glide__shadow--${side}`, h);
      if (!want && have) { have.remove(); delete this.shadowEls[side]; }
    }
    if (o.debug && !this.debugEl) this.debugEl = el('div', 'rr-glide__debug', h);
    if (!o.debug && this.debugEl) { this.debugEl.remove(); this.debugEl = null; }

    if (o.label) {
      this.viewport.setAttribute('role', 'region');
      this.viewport.setAttribute('aria-label', o.label);
    } else if (this.viewport.getAttribute('role') === 'region') {
      this.viewport.removeAttribute('role');
      this.viewport.removeAttribute('aria-label');
    }
    this.paintMarkers();
  }

  // ── Listeners ────────────────────────────────────────────────────────────

  private bind(): void {
    const v = this.viewport;
    const h = this.host;
    const add = <K extends keyof HTMLElementEventMap>(target: HTMLElement | Document, type: K | string, fn: (e: never) => void, opts?: AddEventListenerOptions) => {
      target.addEventListener(type as string, fn as EventListener, opts);
      this.cleanups.push(() => target.removeEventListener(type as string, fn as EventListener, opts));
    };

    add(v, 'scroll', () => this.scheduleFrame(), { passive: true });
    add(v, 'wheel', () => { this.lastSource = 'wheel'; }, { passive: true });
    add(v, 'touchstart', () => { this.lastSource = 'touch'; }, { passive: true });
    add(v, 'keydown', (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) this.lastSource = 'keyboard';
    });
    add(h, 'pointerenter', () => { this.hovering = true; h.classList.add('is-hover'); this.paintVisibility(); });
    add(h, 'pointerleave', () => { this.hovering = false; h.classList.remove('is-hover'); this.paintVisibility(); });
    add(v, 'focusin', () => { this.focused = true; h.classList.add('is-focus'); this.paintVisibility(); });
    add(v, 'focusout', () => { this.focused = false; h.classList.remove('is-focus'); this.paintVisibility(); });

    const startDrag = (axis: 'x' | 'y') => (e: PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const thumb = axis === 'y' ? this.thumbY : this.thumbX;
      thumb.setPointerCapture(e.pointerId);
      this.drag = { axis, start: axis === 'y' ? e.clientY : e.clientX, startScroll: axis === 'y' ? v.scrollTop : this.logicalLeft() };
      this.lastSource = 'thumb';
      h.classList.add('is-dragging');
      this.paintVisibility();
    };
    const moveDrag = (e: PointerEvent) => {
      const d = this.drag;
      if (!d) return;
      const delta = (d.axis === 'y' ? e.clientY : e.clientX) - d.start;
      if (d.axis === 'y') {
        const room = this.track.y - this.track.thumbY;
        if (room > 0) v.scrollTop = d.startScroll + (delta / room) * this.state.maxScrollTop;
      } else {
        const room = this.track.x - this.track.thumbX;
        const logical = this.state.rtl ? -delta : delta;
        if (room > 0) v.scrollLeft = this.toPhysicalLeft(d.startScroll + (logical / room) * this.state.maxScrollLeft);
      }
    };
    const endDrag = () => {
      if (!this.drag) return;
      this.drag = null;
      h.classList.remove('is-dragging');
      this.paintVisibility();
    };
    add(this.thumbY, 'pointerdown', startDrag('y'));
    add(this.thumbX, 'pointerdown', startDrag('x'));
    for (const t of [this.thumbY, this.thumbX]) {
      add(t, 'pointermove', moveDrag);
      add(t, 'pointerup', endDrag);
      add(t, 'pointercancel', endDrag);
    }

    // A press on the rail outside the thumb pages toward the pointer.
    const railJump = (axis: 'x' | 'y') => (e: PointerEvent) => {
      if (e.target !== (axis === 'y' ? this.railY : this.railX)) return;
      e.preventDefault();
      const rail = (axis === 'y' ? this.railY : this.railX).getBoundingClientRect();
      const thumb = (axis === 'y' ? this.thumbY : this.thumbX).getBoundingClientRect();
      this.lastSource = 'rail';
      if (axis === 'y') {
        const dir = e.clientY < thumb.top ? -1 : 1;
        v.scrollBy({ top: dir * v.clientHeight * 0.9, behavior: this.behavior('smooth') });
      } else {
        const before = e.clientX < thumb.left;
        const dir = (this.state.rtl ? !before : before) ? -1 : 1;
        v.scrollBy({ left: this.toPhysicalDelta(dir * v.clientWidth * 0.9), behavior: this.behavior('smooth') });
      }
      void rail;
    };
    add(this.railY, 'pointerdown', railJump('y'));
    add(this.railX, 'pointerdown', railJump('x'));
  }

  private observe(): void {
    if (!this.opts.autoUpdate) return;
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => this.scheduleUpdate());
      this.ro.observe(this.viewport);
      this.ro.observe(this.content);
    }
    if (typeof MutationObserver !== 'undefined') {
      this.mo = new MutationObserver((records) => {
        if (this.opts.preserveScrollPosition) this.noteInsertions(records);
        this.scheduleUpdate();
      });
      this.mo.observe(this.content, { childList: true, subtree: true, characterData: true });
    }
  }

  private unobserve(): void {
    this.ro?.disconnect();
    this.mo?.disconnect();
    this.ro = null;
    this.mo = null;
  }

  /** Was anything inserted above the reader? Then the reader must not move. */
  private noteInsertions(records: MutationRecord[]): void {
    if (this.viewport.scrollTop <= 0) return;
    const top = this.viewport.getBoundingClientRect().top;
    for (const r of records) {
      for (const n of Array.from(r.addedNodes)) {
        if (!(n instanceof Element)) continue;
        const rect = n.getBoundingClientRect();
        if (rect.height > 0 && rect.bottom <= top + 1) { this.addedAbove = true; return; }
      }
    }
  }

  // ── Frames ───────────────────────────────────────────────────────────────

  private scheduleFrame(): void {
    if (this.frameQueued || this.destroyed) return;
    // The flag, not the handle: a frame callback that runs synchronously (a
    // test stub) would otherwise leave a stale handle behind and block the next.
    this.frameQueued = true;
    this.frame = requestAnimationFrame(() => {
      this.frameQueued = false;
      this.frame = null;
      if (this.pendingMeasure) this.update();
      else this.onScrollFrame();
    });
  }

  private scheduleUpdate(): void {
    this.pendingMeasure = true;
    this.scheduleFrame();
  }

  private measure(): void {
    const v = this.viewport;
    const o = this.opts;
    const s = this.state;
    const rtl = getComputedStyle(v).direction === 'rtl';
    const scrollHeight = this.notified.height ?? v.scrollHeight;
    const scrollWidth = this.notified.width ?? v.scrollWidth;
    const clientHeight = v.clientHeight;
    const clientWidth = v.clientWidth;
    const maxTop = Math.max(0, scrollHeight - clientHeight);
    const maxLeft = Math.max(0, scrollWidth - clientWidth);
    const canY = o.axis !== 'x' && maxTop > o.overflowTolerance;
    const canX = o.axis !== 'y' && maxLeft > o.overflowTolerance;

    s.rtl = rtl;
    s.scrollHeight = scrollHeight;
    s.scrollWidth = scrollWidth;
    s.clientHeight = clientHeight;
    s.clientWidth = clientWidth;
    s.maxScrollTop = canY ? maxTop : 0;
    s.maxScrollLeft = canX ? maxLeft : 0;
    s.canScrollY = canY;
    s.canScrollX = canX;

    // Attributes first: the rails are sized by CSS from them, then read once.
    const h = this.host;
    h.toggleAttribute('data-glide-can-y', canY);
    h.toggleAttribute('data-glide-can-x', canX);
    h.setAttribute('data-glide-y', o.verticalPosition === 'auto' ? (rtl ? 'left' : 'right') : o.verticalPosition);
    const size = this.currentSize();
    h.style.setProperty('--rr-glide-corner-y', canX ? `${size}px` : '0px');
    h.style.setProperty('--rr-glide-corner-x', canY ? `${size}px` : '0px');

    this.track.y = canY ? this.railY.clientHeight : 0;
    this.track.x = canX ? this.railX.clientWidth : 0;
    this.track.thumbY = canY ? clamp(this.track.y * clientHeight / scrollHeight, o.minThumbLength, Math.min(o.maxThumbLength, this.track.y)) : 0;
    this.track.thumbX = canX ? clamp(this.track.x * clientWidth / scrollWidth, o.minThumbLength, Math.min(o.maxThumbLength, this.track.x)) : 0;

    if (o.focusable) {
      const want = canY || canX;
      const has = v.hasAttribute('tabindex');
      if (want && !has) v.setAttribute('tabindex', '0');
      if (!want && has && v.getAttribute('tabindex') === '0') v.removeAttribute('tabindex');
    }
    this.readPosition();
  }

  private readPosition(): void {
    const s = this.state;
    s.scrollTop = this.viewport.scrollTop;
    s.scrollLeft = this.logicalLeft();
    s.progressY = s.maxScrollTop > 0 ? clamp(s.scrollTop / s.maxScrollTop, 0, 1) : 0;
    s.progressX = s.maxScrollLeft > 0 ? clamp(s.scrollLeft / s.maxScrollLeft, 0, 1) : 0;
    s.atTop = s.scrollTop <= EDGE;
    s.atBottom = !s.canScrollY || s.maxScrollTop - s.scrollTop <= EDGE;
    s.atLeft = s.scrollLeft <= EDGE;
    s.atRight = !s.canScrollX || s.maxScrollLeft - s.scrollLeft <= EDGE;
  }

  private paint(): void {
    const s = this.state;
    if (s.canScrollY) {
      const room = this.track.y - this.track.thumbY;
      this.thumbY.style.height = `${Math.round(this.track.thumbY)}px`;
      this.thumbY.style.transform = `translate3d(0, ${Math.round(room * s.progressY)}px, 0)`;
    }
    if (s.canScrollX) {
      const room = this.track.x - this.track.thumbX;
      const offset = Math.round(room * s.progressX);
      this.thumbX.style.width = `${Math.round(this.track.thumbX)}px`;
      this.thumbX.style.transform = `translate3d(${s.rtl ? -offset : offset}px, 0, 0)`;
    }
    this.paintVisibility();
    this.paintShadows();
    this.paintDebug();
  }

  private paintVisibility(): void {
    const s = this.state;
    const v = this.opts.visibility;
    const list: GlideVisibility[] = Array.isArray(v) ? v : v === 'auto' ? ['hover', 'scroll', 'focus'] : [v];
    const shown = !list.includes('never') && (
      list.includes('always')
      || (list.includes('hover') && this.hovering)
      || (list.includes('scroll') && s.scrolling)
      || (list.includes('focus') && this.focused)
      || this.drag !== null
    );
    this.railY.classList.toggle('is-visible', shown && s.canScrollY);
    this.railX.classList.toggle('is-visible', shown && s.canScrollX);
  }

  private paintShadows(): void {
    const s = this.state;
    this.shadowEls.top?.classList.toggle('is-visible', s.canScrollY && !s.atTop);
    this.shadowEls.bottom?.classList.toggle('is-visible', s.canScrollY && !s.atBottom);
    this.shadowEls.left?.classList.toggle('is-visible', s.canScrollX && !s.atLeft);
    this.shadowEls.right?.classList.toggle('is-visible', s.canScrollX && !s.atRight);
  }

  private paintMarkers(): void {
    const m = this.markersEl;
    while (m.firstChild) m.removeChild(m.firstChild);
    for (const marker of this.opts.markers) {
      const dot = el('div', `rr-glide__marker${marker.type ? ` rr-glide__marker--${marker.type}` : ''}`, m);
      dot.style.top = `${clamp(marker.position, 0, 1) * 100}%`;
      if (marker.label) dot.title = marker.label;
      dot.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.scrollTo({ top: clamp(marker.position, 0, 1) * this.state.maxScrollTop, behavior: 'smooth' });
      });
    }
  }

  private paintDebug(): void {
    if (!this.debugEl) return;
    const s = this.state;
    const warnings: string[] = [];
    if (!s.clientHeight) warnings.push('Container has no measurable height.');
    if (!s.canScrollX && !s.canScrollY) warnings.push('No overflow: the bars are not needed.');
    if (this.host.parentElement?.closest('.rr-glide')) warnings.push('Nested inside another Glide.');
    this.debugEl.textContent = [
      `Container: ${s.clientWidth} × ${s.clientHeight}`,
      `Content: ${s.scrollWidth} × ${s.scrollHeight}`,
      `Overflow: x ${Math.max(0, s.scrollWidth - s.clientWidth)}  y ${Math.max(0, s.scrollHeight - s.clientHeight)}`,
      `Scroll: ${Math.round(s.scrollLeft)}, ${Math.round(s.scrollTop)}  (${(s.progressY * 100).toFixed(1)}%)`,
      `Thumb: ${Math.round(this.track.thumbY)}px of ${this.track.y}px`,
      `Observer: ${this.ro ? 'active' : 'off'}  Source: ${this.lastSource}  Dir: ${s.rtl ? 'rtl' : 'ltr'}`,
      ...warnings.map((w) => `! ${w}`)
    ].join('\n');
  }

  private onScrollFrame(): void {
    const s = this.state;
    const prevTop = s.scrollTop;
    const prevLeft = s.scrollLeft;
    const prevAtBottom = s.atBottom;
    this.readPosition();
    const dy = s.scrollTop - prevTop;
    const dx = s.scrollLeft - prevLeft;
    s.direction = Math.abs(dy) >= Math.abs(dx)
      ? (dy > 0 ? 'down' : dy < 0 ? 'up' : s.direction)
      : (dx > 0 ? 'right' : 'left');
    // A drag of the thumb is a scroll of the content; the user knows.
    this.wasAtBottom = s.atBottom;

    if (!s.scrolling) {
      s.scrolling = true;
      this.host.classList.add('is-scrolling');
      this.emit('scrolling-change', 'both', 0, 1, this.lastSource);
    }
    if (this.scrollingTimer) clearTimeout(this.scrollingTimer);
    this.scrollingTimer = setTimeout(() => {
      s.scrolling = false;
      this.host.classList.remove('is-scrolling');
      this.paintVisibility();
      this.emit('scrolling-change', 'both', 1, 0, this.lastSource);
    }, this.opts.scrollingThreshold);

    this.paint();

    const source = this.lastSource;
    const throttled = this.throttled();
    if (dy !== 0) {
      if (!throttled) { this.emit('scroll-y', 'y', prevTop, s.scrollTop, source); }
      if (s.atTop && prevTop > EDGE) this.emit('reach-start-y', 'y', prevTop, s.scrollTop, source);
      if (s.atBottom && !prevAtBottom) this.emit('reach-end-y', 'y', prevTop, s.scrollTop, source);
    }
    if (dx !== 0) {
      if (!throttled) this.emit('scroll-x', 'x', prevLeft, s.scrollLeft, source);
      if (s.atLeft && prevLeft > EDGE) this.emit('reach-start-x', 'x', prevLeft, s.scrollLeft, source);
      if (s.atRight && s.maxScrollLeft - prevLeft > EDGE) this.emit('reach-end-x', 'x', prevLeft, s.scrollLeft, source);
    }
    if ((dy !== 0 || dx !== 0) && !throttled) {
      this.emit('scroll', dy !== 0 && dx !== 0 ? 'both' : dy !== 0 ? 'y' : 'x', dy !== 0 ? prevTop : prevLeft, dy !== 0 ? s.scrollTop : s.scrollLeft, source);
      this.emit('progress', 'both', 0, s.progressY, source);
    }
    if (s.atBottom !== prevAtBottom) this.emit('bottom-state-change', 'y', prevAtBottom ? 1 : 0, s.atBottom ? 1 : 0, source);
    this.infiniteCheck(source);
    if (!throttled) this.lastEmit = performance.now();
    this.lastSource = 'unknown';
  }

  private throttled(): boolean {
    const t = this.opts.throttle;
    if (typeof t !== 'number') return false;
    return performance.now() - this.lastEmit < t;
  }

  /** After a measurement: keep the reader where they were, or at the bottom. */
  private afterMeasure(): void {
    const s = this.state;
    const v = this.viewport;
    const grew = s.scrollHeight - this.prevScrollHeight;
    if (this.opts.preserveScrollPosition && this.addedAbove && grew > 0 && v.scrollTop > 0 && !(this.opts.stickToBottom && this.wasAtBottom)) {
      this.lastSource = 'programmatic';
      v.scrollTop += grew;
      this.readPosition();
      this.paint();
    }
    this.addedAbove = false;
    if (this.opts.stickToBottom === 'always' || (this.opts.stickToBottom === 'smart' && this.wasAtBottom && grew !== 0)) {
      if (!s.atBottom) {
        this.lastSource = 'programmatic';
        v.scrollTop = v.scrollHeight;
        this.readPosition();
        this.paint();
      }
    }
    const atBottomChanged = s.atBottom !== this.wasAtBottom;
    this.wasAtBottom = s.atBottom;
    this.prevScrollHeight = s.scrollHeight;
    this.emit('resize', 'both', 0, 0, 'unknown');
    if (atBottomChanged) this.emit('bottom-state-change', 'y', s.atBottom ? 0 : 1, s.atBottom ? 1 : 0, 'unknown');
    this.infiniteCheck('unknown');
  }

  private infiniteCheck(source: GlideSource): void {
    const inf = this.opts.infiniteScroll;
    if (!inf || this.infinite.pending) return;
    const s = this.state;
    const threshold = inf.threshold ?? 200;
    const call = (fn: () => void | Promise<unknown>) => {
      const result = fn();
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        this.infinite.pending = true;
        (result as Promise<unknown>).then(() => { this.infinite.pending = false; }, () => { this.infinite.pending = false; });
      }
    };
    if (inf.onReachEnd && s.canScrollY && s.maxScrollTop - s.scrollTop <= threshold && this.infinite.endHeight !== s.scrollHeight) {
      this.infinite.endHeight = s.scrollHeight;
      call(inf.onReachEnd);
      return;
    }
    if (inf.onReachStart && s.canScrollY && s.scrollTop <= threshold && this.infinite.startHeight !== s.scrollHeight && source !== 'unknown') {
      this.infinite.startHeight = s.scrollHeight;
      call(inf.onReachStart);
    }
  }

  private emit(type: GlideEventName, axis: GlideEvent['axis'], previous: number, current: number, source: GlideSource): void {
    const event: GlideEvent = {
      type, axis, direction: this.state.direction, previous, current, delta: current - previous,
      progress: axis === 'x' ? this.state.progressX : this.state.progressY, source, state: this.getState()
    };
    this.handlers.get(type)?.forEach((h) => h(event));
    this.host.dispatchEvent(new CustomEvent(`glide:${type}`, { detail: event }));
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Distance scrolled from the start edge, whatever the writing direction. */
  private logicalLeft(): number {
    const raw = this.viewport.scrollLeft;
    return this.state.rtl ? Math.abs(raw) : raw;
  }

  private toPhysicalLeft(logical: number): number {
    return this.state.rtl ? -logical : logical;
  }

  private toPhysicalDelta(logical: number): number {
    return this.state.rtl ? -logical : logical;
  }

  private currentSize(): number {
    const raw = getComputedStyle(this.host).getPropertyValue('--rr-glide-size').trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 8;
  }

  private behavior(b?: GlideBehavior): ScrollBehavior {
    if (b === 'instant') return 'auto';
    if (b === 'smooth') return reducedMotion() ? 'auto' : 'smooth';
    return 'auto';
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function nearest(scroll: number, size: number, start: number, length: number, offset: number): number {
  if (start - offset < scroll) return start - offset;
  if (start + length + offset > scroll + size) return start + length + offset - size;
  return scroll;
}

export type { GlideAxis, GlideOptions, GlideState, GlideEvent, GlideEventName, GlideHandler } from './types.js';
